/**
 * Companion minutes, in Settings.
 *
 * One card, drawn from GET /api/me/credits: how much of this month's
 * allowance is used, what was bought on top, and the three credit packs.
 * The packs are a draft until Michael approves them and connects checkout,
 * and the card says so. "Get more minutes" asks the Worker for a checkout
 * link; while none is connected the Worker answers 501 and the card says
 * "Coming soon" instead of pretending.
 *
 * mountCredits(container) draws the card into its own #creditsRoot inside
 * the container, right under the account card, so app.html is untouched.
 * creditsRemaining() is for other modules: the seconds left as of the last
 * load, or null when the card has never loaded.
 */

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const mins = (sec) => Math.floor(Math.max(0, Number(sec) || 0) / 60);
const price = (usd) => (usd == null ? 'Price coming soon' : `$${Number(usd).toFixed(2)}`);

let root = null;
let state = null;          // the last /api/me/credits answer
let chosen = null;         // pack id the player picked

export function creditsRemaining() {
  return state ? Number(state.remainingSec) || 0 : null;
}

export async function mountCredits(container) {
  if (!container) return;
  root = container.querySelector('#creditsRoot');
  if (!root) {
    root = document.createElement('div');
    root.id = 'creditsRoot';
    const after = container.querySelector('#accountRoot');
    if (after) after.insertAdjacentElement('afterend', root); else container.appendChild(root);
  }
  await load();
}

async function load() {
  try {
    const r = await fetch('/api/me/credits', { headers: { accept: 'application/json' } });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.error || `request failed (${r.status})`);
    state = body;
    if (!chosen || !(state.packs || []).some((p) => p.id === chosen)) chosen = state.packs?.[0]?.id || null;
    paint();
  } catch (e) {
    root.innerHTML = card(`<p class="credits-err">${esc(e.message)}</p>`);
  }
}

const card = (inner) => `<div class="card credits" style="margin-bottom:14px"><h3>Companion minutes</h3>${inner}</div>`;

function paint() {
  const used = mins(state.usedSec), inc = mins(state.includedSec), bought = mins(state.purchasedSec), left = mins(state.remainingSec);
  const pct = inc ? Math.min(100, Math.round(100 * Math.min(state.usedSec, state.includedSec) / state.includedSec)) : 100;
  const packs = (state.packs || []).map((p) => `
    <button type="button" class="credits-pack${p.id === chosen ? ' on' : ''}" data-pack="${esc(p.id)}">
      <b>${esc(p.minutes)} min</b>
      <span>${esc(price(p.priceUsd))}</span>
      <small>${esc(p.name)}${p.draft && p.priceUsd != null ? ' &middot; draft price' : ''}</small>
    </button>`).join('');
  root.innerHTML = card(`
    <div class="credits-meter">
      <div class="credits-bar"><i style="width:${pct}%"></i></div>
      <div class="credits-line"><b>${used} of ${inc} minutes</b><small>used this month${bought ? `, plus ${bought} purchased` : ''}</small></div>
    </div>
    <p class="credits-note">${state.remainingSec > 0
      ? `${left ? `${left} minute${left === 1 ? '' : 's'}` : 'Under a minute'} left. Talking, listening and the spoken replies all count; shot scoring and reactions never do.`
      : 'Nothing left this month. Shot scoring and reactions keep working; the meter refills next month, or get more minutes below.'}</p>
    <div class="credits-packs">${packs}</div>
    <div class="credits-row">
      <button type="button" class="btn primary" id="creditsBuy" ${chosen ? '' : 'disabled'}>Get more minutes</button>
      <small class="credits-note">Prices are a draft. Checkout is not connected yet.</small>
    </div>`);
  root.querySelectorAll('[data-pack]').forEach((b) => { b.onclick = () => { chosen = b.dataset.pack; paint(); }; });
  const buy = root.querySelector('#creditsBuy');
  if (buy) buy.onclick = () => checkout(buy);
}

async function checkout(button) {
  if (!chosen) return;
  button.disabled = true;
  // Opened now, inside the tap: a window opened after an await is a popup to
  // iOS and gets blocked. It is pointed at checkout once the URL comes back,
  // and closed again if there is none.
  const tab = window.open('', '_blank');
  try {
    const r = await fetch('/api/credits/checkout', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ pack: chosen }) });
    const body = await r.json().catch(() => ({}));
    if (r.status === 501) { tab?.close(); toast('Coming soon'); return; }
    if (!r.ok) throw new Error(body.error || `request failed (${r.status})`);
    if (!body.url) { tab?.close(); return; }
    if (tab) { tab.opener = null; tab.location.href = body.url; } else location.href = body.url;
  } catch (e) { tab?.close(); toast(e.message); }
  finally { button.disabled = false; }
}

/** The shell's toast element, when the page has one; a console line when it does not. */
function toast(msg) {
  const el = document.getElementById('toast');
  if (!el) { console.info(msg); return; }
  el.textContent = msg;
  el.className = 'toast show';
  clearTimeout(toast.t);
  toast.t = setTimeout(() => (el.className = 'toast'), 3400);
}
